import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { PawIcon, ClockIcon, TagIcon, CalendarIcon, ShieldCheckIcon } from "./Icons";

// ─── Inline icons ──────────────────────────────────────────────────────────────
const DogIcon = ({ size = 24, color = "currentColor", className = "" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M10 5.172C10 3.782 8.423 2.679 6.5 3c-2 .336-3.5 2.112-3.5 4-.085 2.053.416 3.461 1.5 4.5s1 3.5 0 4.5"/>
    <path d="m10 5.172 2 3.792c0 .01 2.36 5.036 2 6.036-1 2-4 2-5 0s-.5-4 0-5"/>
    <path d="M16.5 12s.5 2 3 3c0 3-3 5-6 4.5s-3-3.5-3-3.5"/>
    <path d="M3 12s.5-1 1-2"/><path d="M5 3.5s.5-1 2-1c.83 0 1.5.5 1.5.5"/>
  </svg>
);

const CatIcon = ({ size = 24, color = "currentColor", className = "" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M12 5c.67 0 1.35.09 2 .26 1.78-2 5.03-2.84 6.42-2.26 1.4.58-.42 7-.42 7 .57 1.07 1 2.24 1 3.44C21 17.9 16.97 21 12 21s-9-3-9-7.56c0-1.25.5-2.4 1-3.44 0 0-1.89-6.42-.5-7 1.39-.58 4.72.23 6.5 2.23A9.04 9.04 0 0 1 12 5z"/>
    <path d="M8 14v.5"/><path d="M16 14v.5"/>
    <path d="M11.25 16.25h1.5L12 17l-.75-.75z"/>
  </svg>
);

const AlertIcon = ({ size = 24, color = "currentColor", className = "" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
    <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
  </svg>
);

const PhoneIcon = ({ size = 15, color = "currentColor", className = "" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
  </svg>
);

const MailIcon = ({ size = 15, color = "currentColor", className = "" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className={className}>
    <rect width="20" height="16" x="2" y="4" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/>
  </svg>
);

const EditIcon = ({ size = 15, color = "currentColor", className = "" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className={className}>
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
  </svg>
);

const ChevronDownIcon = ({ size = 16, color = "currentColor", className = "" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" className={className}>
    <polyline points="6 9 12 15 18 9"/>
  </svg>
);

// ─── Age helpers ───────────────────────────────────────────────────────────────
const getRealAgeYears = (birthday) => {
  if (!birthday) return null;
  return (Date.now() - new Date(birthday).getTime()) / (1000 * 60 * 60 * 24 * 365.25);
};

const formatRealAge = (realYears) => {
  if (realYears === null) return "Unknown";
  const totalMonths = Math.round(realYears * 12);
  if (totalMonths < 12) return `${totalMonths} month${totalMonths !== 1 ? "s" : ""}`;
  const yrs = Math.floor(realYears);
  const mos = Math.round((realYears - yrs) * 12);
  return mos > 0 ? `${yrs} yr ${mos} mo` : `${yrs} year${yrs !== 1 ? "s" : ""}`;
};

// ─── Status configs ────────────────────────────────────────────────────────────
const STATUS_CONFIG = {
  pending: {
    label: "Pending Approval",
    bg: "bg-amber-100",
    text: "text-amber-800",
    border: "border-amber-300",
    badgeBg: "bg-amber-500",
    emoji: "⏳",
  },
  confirmed: {
    label: "Confirmed",
    bg: "bg-blue-100",
    text: "text-blue-800",
    border: "border-blue-300",
    badgeBg: "bg-blue-600",
    emoji: "✅",
  },
  ongoing: {
    label: "On Going (In Service)",
    bg: "bg-purple-100",
    text: "text-purple-800",
    border: "border-purple-300",
    badgeBg: "bg-purple-600",
    emoji: "✂️",
  },
  completed: {
    label: "Completed",
    bg: "bg-emerald-100",
    text: "text-emerald-800",
    border: "border-emerald-300",
    badgeBg: "bg-emerald-600",
    emoji: "🎉",
  },
  cancelled: {
    label: "Cancelled",
    bg: "bg-gray-100",
    text: "text-gray-700",
    border: "border-gray-300",
    badgeBg: "bg-gray-500",
    emoji: "❌",
  },
  rejected: {
    label: "Rejected",
    bg: "bg-red-100",
    text: "text-red-800",
    border: "border-red-300",
    badgeBg: "bg-red-600",
    emoji: "🚫",
  },
  reschedule_requested: {
    label: "Reschedule Requested",
    bg: "bg-orange-100",
    text: "text-orange-800",
    border: "border-orange-300",
    badgeBg: "bg-orange-500",
    emoji: "🔄",
  },
  cancel_requested: {
    label: "Cancel Requested",
    bg: "bg-red-100",
    text: "text-red-800",
    border: "border-red-300",
    badgeBg: "bg-red-500",
    emoji: "⚠️",
  },
};

const ACTIVE_STATUSES = ["pending", "confirmed", "ongoing", "reschedule_requested", "cancel_requested"];

// ─── PetInfoModal ──────────────────────────────────────────────────────────────
const PetInfoModal = ({
  userId,
  petId,
  appointmentId,
  userToken,
  onClose,
  onViewBookings,
  onEditAppointment,
  onStatusUpdate,
}) => {
  const [pet, setPet] = useState(null);
  const [ownerInfo, setOwnerInfo] = useState({ name: "", email: "", phone: "" });
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState("current"); // "current" | "details" | "history"
  const [expandedHistoryId, setExpandedHistoryId] = useState(null);
  const [zoomImage, setZoomImage] = useState(null);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);

  // Helper to extract pet-specific details from an appointment
  const getPetDetailsInAppt = (appt, petObj) => {
    if (!appt || !appt.pets) return null;
    if (petObj && petObj._id) {
      const match = appt.pets.find(
        (p) =>
          (p.pet && p.pet.toString() === petObj._id.toString()) ||
          (p.petName && p.petName.toLowerCase() === petObj.petName?.toLowerCase())
      );
      if (match) return match;
    }
    return appt.pets[0] || null;
  };

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      let effectivePetId = petId;
      let effectiveUserId = userId;

      // If appointmentId is passed directly without petId, fetch appointment first
      if (appointmentId && !effectivePetId) {
        try {
          const singleApptRes = await axios.get(`/api/appointments/${appointmentId}`, {
            headers: userToken ? { Authorization: `Bearer ${userToken}` } : {},
          });
          const fetchedAppt = singleApptRes.data.data;
          if (fetchedAppt) {
            effectiveUserId = fetchedAppt.user?._id || fetchedAppt.user || effectiveUserId;
            const p0 = fetchedAppt.pets?.[0];
            effectivePetId = p0?.pet?._id || p0?.pet || effectivePetId;
          }
        } catch (_) {}
      }

      // Fetch pet profile
      if (effectivePetId) {
        const petRes = await axios.get(`/api/auth/users/${effectiveUserId || "u"}/pets/${effectivePetId}/info`);
        setPet(petRes.data.pet);
        setOwnerInfo({
          name: petRes.data.ownerName || petRes.data.pet?.owner?.name || "Customer",
          email: petRes.data.ownerEmail || petRes.data.pet?.owner?.email || "",
          phone: petRes.data.ownerPhone || petRes.data.pet?.owner?.phone || "",
        });

        // Fetch pet appointments
        if (userToken) {
          const apptRes = await axios.get(
            `/api/auth/users/${effectiveUserId || "u"}/pets/${effectivePetId}/appointments`,
            { headers: { Authorization: `Bearer ${userToken}` } }
          );
          const apptsList = apptRes.data.data || [];
          setAppointments(apptsList);

          // Smart initial tab: if active appointment exists, focus on "current", else "details"
          const hasActive = apptsList.some((a) => ACTIVE_STATUSES.includes(a.status));
          if (hasActive || appointmentId) {
            setTab("current");
          } else {
            setTab("details");
          }
        }
      } else {
        setError("Could not find a valid pet associated with this QR code.");
      }
    } catch (err) {
      console.error("Error loading scanned pet profile:", err);
      setError("Could not load pet information. The QR code may be invalid or expired.");
    } finally {
      setLoading(false);
    }
  }, [userId, petId, appointmentId, userToken]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // ─── Status Actions inside Modal ───────────────────────────────────────────
  const handleQuickStatusChange = async (apptId, newStatus, extra = {}) => {
    try {
      setIsUpdatingStatus(true);
      await axios.put(
        `/api/appointments/${apptId}`,
        { status: newStatus, ...extra },
        { headers: { Authorization: `Bearer ${userToken}` } }
      );
      setAppointments((prev) =>
        prev.map((a) => (a._id === apptId ? { ...a, status: newStatus, ...extra } : a))
      );
      if (onStatusUpdate) onStatusUpdate();
    } catch (err) {
      alert("Failed to update status: " + (err.response?.data?.message || err.message));
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const handleTogglePickupStatus = async (apptId, currentReady) => {
    try {
      setIsUpdatingStatus(true);
      const nextReady = !currentReady;
      await axios.put(
        `/api/appointments/${apptId}`,
        { isReadyForPickup: nextReady },
        { headers: { Authorization: `Bearer ${userToken}` } }
      );
      setAppointments((prev) =>
        prev.map((a) => (a._id === apptId ? { ...a, isReadyForPickup: nextReady } : a))
      );
      if (onStatusUpdate) onStatusUpdate();
    } catch (err) {
      alert("Failed to update pickup status: " + (err.response?.data?.message || err.message));
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  const realAge = pet ? getRealAgeYears(pet.birthday) : null;

  // Active / Current appointment identification:
  // If a specific appointmentId was scanned, favor it; otherwise take the newest active appointment
  const currentAppt =
    (appointmentId && appointments.find((a) => a._id === appointmentId)) ||
    appointments.find((a) => ACTIVE_STATUSES.includes(a.status)) ||
    null;

  // Past appointments (exclude the current one to prevent duplicate display)
  const pastAppointments = appointments.filter((a) => a._id !== currentAppt?._id);
  const completedVisits = appointments.filter((a) => a.status === "completed").length;

  const currentPetApptData = currentAppt ? getPetDetailsInAppt(currentAppt, pet) : null;
  const currentStatusCfg = currentAppt ? STATUS_CONFIG[currentAppt.status] || STATUS_CONFIG.pending : null;

  return (
    <>
      <div
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-3 sm:p-4 overflow-y-auto"
        onClick={handleOverlayClick}
      >
        <div className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl max-h-[92vh] flex flex-col relative overflow-hidden animate-in fade-in zoom-in-95 duration-200 border border-purple-100 my-auto">
          
          {/* ── Top Clinic Header Bar ── */}
          <div className="bg-gradient-to-r from-[#1b2d24] via-[#284033] to-[#1b2d24] text-white px-6 py-4 flex items-center justify-between shrink-0 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-white/10 flex items-center justify-center text-lg shadow-inner">
                📷
              </div>
              <div>
                <h3 className="text-base font-black tracking-tight text-white flex items-center gap-2">
                  Pet &amp; Booking Scanner
                  <span className="text-[10px] bg-emerald-500/20 text-emerald-300 font-bold px-2 py-0.5 rounded-full border border-emerald-400/30 uppercase tracking-wider">
                    Verified QR
                  </span>
                </h3>
                <p className="text-[11px] text-gray-300 font-medium">
                  Furpect Tails Reception Check-In Desk
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white font-bold text-lg transition-all"
              title="Close modal"
            >
              ×
            </button>
          </div>

          {/* ── Main Scrollable Body ── */}
          <div className="flex-1 overflow-y-auto p-5 sm:p-6 space-y-5 bg-[#faf9f7]">
            {loading ? (
              <div className="text-center py-20 text-[#46735c] font-bold space-y-3">
                <div className="animate-spin rounded-full h-11 w-11 border-4 border-[#46735c] border-t-transparent mx-auto" />
                <p className="text-sm">Pulling up pet profile and visit records…</p>
              </div>
            ) : error ? (
              <div className="text-center py-16 text-red-500 font-bold space-y-3 bg-red-50 rounded-2xl p-6 border border-red-200">
                <div className="flex justify-center">
                  <AlertIcon size={44} color="#ef4444" />
                </div>
                <p className="text-sm text-gray-800">{error}</p>
                <button
                  onClick={onClose}
                  className="px-5 py-2.5 bg-red-600 text-white rounded-xl text-xs font-bold shadow hover:bg-red-700 transition-all"
                >
                  Close Scanner
                </button>
              </div>
            ) : pet ? (
              <>
                {/* ── Pet & Owner Hero Profile Card ── */}
                <div className="bg-white rounded-2xl p-4 sm:p-5 border border-purple-100 shadow-sm flex flex-col sm:flex-row items-center sm:items-start gap-4">
                  {/* Avatar */}
                  <div className="relative shrink-0">
                    {pet.photoUrl ? (
                      <img
                        src={pet.photoUrl}
                        alt={pet.petName}
                        onClick={() => setZoomImage(pet.photoUrl)}
                        className="w-20 h-20 sm:w-24 sm:h-24 rounded-2xl object-cover border-4 border-purple-100 shadow-md cursor-zoom-in hover:scale-105 transition-transform"
                      />
                    ) : (
                      <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-2xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white shadow-md">
                        {pet.petType === "cat" ? <CatIcon size={40} color="white" /> : <DogIcon size={40} color="white" />}
                      </div>
                    )}
                    <span className="absolute -bottom-2 -right-2 bg-white text-xs p-1 rounded-full shadow border border-gray-100">
                      {pet.petType === "cat" ? "🐱" : "🐶"}
                    </span>
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0 text-center sm:text-left space-y-1">
                    <div className="flex flex-wrap items-center justify-center sm:justify-start gap-2">
                      <h2 className="text-2xl font-black text-gray-800 tracking-tight leading-tight">
                        {pet.petName}
                      </h2>
                      <span className="text-[11px] font-extrabold uppercase px-2.5 py-0.5 rounded-full bg-purple-100 text-purple-700">
                        {pet.petType} · {pet.gender === "male" ? "♂ Male" : pet.gender === "female" ? "♀ Female" : "Unknown"}
                      </span>
                    </div>

                    <p className="text-xs text-gray-500 font-medium">
                      Breed: <span className="font-bold text-gray-700">{pet.breed || "Mixed Breed"}</span> · Age:{" "}
                      <span className="font-bold text-gray-700">{formatRealAge(realAge)}</span>
                    </p>

                    {/* Owner details pill */}
                    <div className="pt-1.5 flex flex-wrap items-center justify-center sm:justify-start gap-2 text-xs">
                      <span className="font-bold text-gray-700 flex items-center gap-1 bg-gray-100 px-2.5 py-1 rounded-lg">
                        👤 Owner: {ownerInfo.name || "Customer"}
                      </span>
                      {ownerInfo.phone && (
                        <a
                          href={`tel:${ownerInfo.phone}`}
                          className="text-[#46735c] font-bold hover:underline flex items-center gap-1 bg-emerald-50 px-2.5 py-1 rounded-lg border border-emerald-100"
                        >
                          <PhoneIcon size={12} color="#46735c" />
                          {ownerInfo.phone}
                        </a>
                      )}
                      {ownerInfo.email && (
                        <a
                          href={`mailto:${ownerInfo.email}`}
                          className="text-gray-500 hover:text-gray-700 flex items-center gap-1 bg-gray-50 px-2.5 py-1 rounded-lg border border-gray-200"
                        >
                          <MailIcon size={12} />
                          {ownerInfo.email}
                        </a>
                      )}
                    </div>
                  </div>

                  {/* Quick stats badge */}
                  <div className="shrink-0 flex sm:flex-col items-center justify-center bg-purple-50/70 border border-purple-100 rounded-2xl p-3 text-center min-w-[90px]">
                    <span className="text-[10px] font-black uppercase tracking-wider text-purple-600 block">
                      Visits
                    </span>
                    <span className="text-2xl font-black text-purple-900 leading-none my-0.5">
                      {completedVisits}
                    </span>
                    <span className="text-[10px] text-gray-500 font-bold block">
                      {appointments.length} booked
                    </span>
                  </div>
                </div>

                {/* ── 3 Primary Tabs Navigation ── */}
                <div className="flex gap-2 p-1 bg-gray-200/60 rounded-2xl">
                  {/* Tab 1: Current Appointment */}
                  <button
                    onClick={() => setTab("current")}
                    className={`flex-1 py-2.5 px-3 rounded-xl text-xs font-black transition-all flex items-center justify-center gap-1.5 ${
                      tab === "current"
                        ? "bg-white text-purple-700 shadow-md scale-[1.01]"
                        : "text-gray-600 hover:text-gray-900 hover:bg-white/40"
                    }`}
                  >
                    <span>⚡</span>
                    <span>Current Appointment</span>
                    {currentAppt ? (
                      <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse ml-0.5" />
                    ) : (
                      <span className="text-[10px] opacity-60 font-semibold">(None)</span>
                    )}
                  </button>

                  {/* Tab 2: Details */}
                  <button
                    onClick={() => setTab("details")}
                    className={`flex-1 py-2.5 px-3 rounded-xl text-xs font-black transition-all flex items-center justify-center gap-1.5 ${
                      tab === "details"
                        ? "bg-white text-purple-700 shadow-md scale-[1.01]"
                        : "text-gray-600 hover:text-gray-900 hover:bg-white/40"
                    }`}
                  >
                    <span>🐾</span>
                    <span>Details</span>
                  </button>

                  {/* Tab 3: History */}
                  <button
                    onClick={() => setTab("history")}
                    className={`flex-1 py-2.5 px-3 rounded-xl text-xs font-black transition-all flex items-center justify-center gap-1.5 ${
                      tab === "history"
                        ? "bg-white text-purple-700 shadow-md scale-[1.01]"
                        : "text-gray-600 hover:text-gray-900 hover:bg-white/40"
                    }`}
                  >
                    <span>📜</span>
                    <span>History ({pastAppointments.length})</span>
                  </button>
                </div>

                {/* ════════════════════════════════════════════════════════════════
                    TAB 1: CURRENT APPOINTMENT
                ════════════════════════════════════════════════════════════════ */}
                {tab === "current" && (
                  <div className="space-y-4 animate-in fade-in duration-200">
                    {currentAppt ? (
                      <div className="bg-white rounded-3xl p-5 border-2 border-purple-200 shadow-sm space-y-4">
                        {/* Live Status Header Banner */}
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 rounded-2xl bg-gradient-to-r from-purple-50 via-pink-50 to-purple-50 border border-purple-200">
                          <div className="flex items-center gap-3">
                            <span className="text-2xl">{currentStatusCfg?.emoji || "📅"}</span>
                            <div>
                              <div className="flex items-center gap-2">
                                <span className="text-xs font-black uppercase tracking-wider text-purple-700">
                                  Current Status
                                </span>
                                <span
                                  className={`text-[10px] font-black uppercase px-2.5 py-0.5 rounded-full ${
                                    currentStatusCfg?.bg
                                  } ${currentStatusCfg?.text}`}
                                >
                                  {currentStatusCfg?.label}
                                </span>
                              </div>
                              <p className="text-sm font-black text-gray-800 mt-0.5">
                                {currentAppt.appointmentDate
                                  ? new Date(currentAppt.appointmentDate).toLocaleDateString("en-US", {
                                      weekday: "long",
                                      month: "short",
                                      day: "numeric",
                                      year: "numeric",
                                    })
                                  : currentPetApptData?.grooming?.date
                                  ? new Date(currentPetApptData.grooming.date).toLocaleDateString("en-US", {
                                      weekday: "long",
                                      month: "short",
                                      day: "numeric",
                                    })
                                  : "Date Not Specified"}
                                {(currentAppt.appointmentTime || currentPetApptData?.grooming?.time) && (
                                  <span className="text-purple-700 font-bold ml-1.5">
                                    at {currentAppt.appointmentTime || currentPetApptData?.grooming?.time}
                                  </span>
                                )}
                              </p>
                            </div>
                          </div>

                          {/* Ready for Pickup badge */}
                          {currentAppt.isReadyForPickup && (
                            <div className="bg-emerald-50 border border-emerald-300 text-emerald-800 text-xs font-black px-3 py-1.5 rounded-xl flex items-center gap-1.5 shadow-sm animate-pulse">
                              <span>🛍️</span> Ready for Pickup
                            </div>
                          )}
                        </div>

                        {/* Reschedule banner if requested */}
                        {currentAppt.status === "reschedule_requested" && currentAppt.rescheduleRequestedDate && (
                          <div className="p-3.5 bg-orange-50 rounded-2xl border border-orange-200 text-xs space-y-1">
                            <div className="flex items-center justify-between">
                              <span className="font-black text-orange-900 flex items-center gap-1">
                                🔄 Customer Requested Reschedule
                              </span>
                              <span className="text-[10px] font-bold bg-orange-200 text-orange-900 px-2 py-0.5 rounded-full">
                                Action Required
                              </span>
                            </div>
                            <div className="grid grid-cols-2 gap-2 text-[11px] pt-1">
                              <div>
                                <span className="text-gray-400 font-bold uppercase block text-[9px]">Original:</span>
                                <span className="line-through text-gray-600 font-semibold">
                                  {currentAppt.appointmentDate
                                    ? new Date(currentAppt.appointmentDate).toLocaleDateString()
                                    : "N/A"}{" "}
                                  {currentAppt.appointmentTime}
                                </span>
                              </div>
                              <div>
                                <span className="text-orange-700 font-bold uppercase block text-[9px]">
                                  Requested To:
                                </span>
                                <span className="font-black text-orange-950">
                                  📅 {new Date(currentAppt.rescheduleRequestedDate).toLocaleDateString()}{" "}
                                  {currentAppt.rescheduleRequestedTime ? `at ${currentAppt.rescheduleRequestedTime}` : ""}
                                </span>
                              </div>
                            </div>
                            {currentAppt.rescheduleReason && (
                              <p className="text-[11px] text-orange-900 pt-1">
                                <strong>Reason:</strong> {currentAppt.rescheduleReason}
                              </p>
                            )}
                          </div>
                        )}

                        {/* Services Breakdown */}
                        <div className="space-y-3">
                          <p className="text-xs font-black uppercase tracking-wider text-gray-400">
                            Service Specifications
                          </p>

                          {/* Grooming Details */}
                          {currentPetApptData?.grooming && currentPetApptData.grooming.service && (
                            <div className="p-4 bg-purple-50/70 rounded-2xl border border-purple-100 space-y-2.5">
                              <div className="flex items-center justify-between">
                                <span className="text-xs font-black text-purple-800 flex items-center gap-1.5">
                                  <span>✂️</span> Grooming Package
                                </span>
                                <span className="text-xs font-black text-purple-900 bg-purple-200/60 px-2.5 py-0.5 rounded-full">
                                  ₱{(currentPetApptData.grooming.price || 0).toLocaleString()}
                                </span>
                              </div>
                              <p className="text-sm font-extrabold text-gray-800">
                                {currentPetApptData.grooming.service}
                              </p>

                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
                                {currentPetApptData.grooming.haircutStyle && (
                                  <div className="bg-white p-2.5 rounded-xl border border-purple-100">
                                    <span className="text-[10px] font-bold text-gray-400 uppercase block">
                                      Haircut Style
                                    </span>
                                    <span className="font-bold text-gray-800">
                                      {currentPetApptData.grooming.haircutStyle}
                                    </span>
                                  </div>
                                )}
                                {currentPetApptData.grooming.size && (
                                  <div className="bg-white p-2.5 rounded-xl border border-purple-100">
                                    <span className="text-[10px] font-bold text-gray-400 uppercase block">
                                      Size Category
                                    </span>
                                    <span className="font-bold text-gray-800 capitalize">
                                      {currentPetApptData.grooming.size}
                                    </span>
                                  </div>
                                )}
                              </div>

                              {currentPetApptData.grooming.haircutInstructions && (
                                <div className="bg-white p-3 rounded-xl border border-purple-100 text-xs">
                                  <span className="text-[10px] font-bold text-purple-600 uppercase block mb-0.5">
                                    Special Grooming Instructions
                                  </span>
                                  <p className="text-gray-700 italic">
                                    "{currentPetApptData.grooming.haircutInstructions}"
                                  </p>
                                </div>
                              )}

                              {/* Add-ons */}
                              {currentPetApptData.grooming.addOns && currentPetApptData.grooming.addOns.length > 0 && (
                                <div className="bg-white p-3 rounded-xl border border-purple-100 text-xs space-y-1">
                                  <span className="text-[10px] font-bold text-gray-400 uppercase block mb-1">
                                    Selected Add-Ons
                                  </span>
                                  <div className="flex flex-wrap gap-1.5">
                                    {currentPetApptData.grooming.addOns.map((add, idx) => (
                                      <span
                                        key={idx}
                                        className="inline-flex items-center gap-1 bg-purple-100/70 text-purple-800 px-2.5 py-1 rounded-lg text-xs font-bold"
                                      >
                                        ✨ {add.name} {add.price ? `(₱${add.price})` : ""}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          )}

                          {/* Hotel Details */}
                          {currentPetApptData?.hotel && currentPetApptData.hotel.roomType && (
                            <div className="p-4 bg-amber-50/70 rounded-2xl border border-amber-100 space-y-2.5">
                              <div className="flex items-center justify-between">
                                <span className="text-xs font-black text-amber-800 flex items-center gap-1.5">
                                  <span>🏨</span> Hotel Boarding
                                </span>
                                <span className="text-xs font-black text-amber-900 bg-amber-200/60 px-2.5 py-0.5 rounded-full">
                                  ₱{(currentPetApptData.hotel.totalPrice || 0).toLocaleString()}
                                </span>
                              </div>
                              <p className="text-sm font-extrabold text-gray-800">
                                {currentPetApptData.hotel.roomType} (
                                {currentPetApptData.hotel.stayDays || 1} day
                                {(currentPetApptData.hotel.stayDays || 1) > 1 ? "s" : ""})
                              </p>

                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
                                <div className="bg-white p-2.5 rounded-xl border border-amber-100">
                                  <span className="text-[10px] font-bold text-gray-400 uppercase block">Check-In</span>
                                  <span className="font-bold text-gray-800">
                                    {currentPetApptData.hotel.checkInDate
                                      ? new Date(currentPetApptData.hotel.checkInDate).toLocaleDateString()
                                      : "N/A"}{" "}
                                    at {currentPetApptData.hotel.checkInTime || "10:00 AM"}
                                  </span>
                                </div>
                                <div className="bg-white p-2.5 rounded-xl border border-amber-100">
                                  <span className="text-[10px] font-bold text-gray-400 uppercase block">Check-Out</span>
                                  <span className="font-bold text-gray-800">
                                    {currentPetApptData.hotel.checkOutDate
                                      ? new Date(currentPetApptData.hotel.checkOutDate).toLocaleDateString()
                                      : "N/A"}{" "}
                                    at {currentPetApptData.hotel.checkOutTime || "12:00 PM"}
                                  </span>
                                </div>
                              </div>

                              {Boolean(currentPetApptData.hotel.exceedingHours) && (
                                <div className="bg-white p-2.5 rounded-xl border border-amber-200 text-xs text-amber-800 font-bold">
                                  ⏱️ Exceeding Hours: +{currentPetApptData.hotel.exceedingHours} hr(s) (Fee: ₱
                                  {(
                                    currentPetApptData.hotel.exceedingFee ||
                                    currentPetApptData.hotel.exceedingHours * 50
                                  ).toLocaleString()}
                                  )
                                </div>
                              )}

                              {currentPetApptData.hotel.feedingInstructions && (
                                <div className="bg-white p-3 rounded-xl border border-amber-100 text-xs">
                                  <span className="text-[10px] font-bold text-amber-600 uppercase block mb-0.5">
                                    Feeding &amp; Care Notes
                                  </span>
                                  <p className="text-gray-700 italic">
                                    "{currentPetApptData.hotel.feedingInstructions}"
                                  </p>
                                </div>
                              )}
                            </div>
                          )}

                          {/* Fallback if neither grooming nor hotel specific object is filled */}
                          {!currentPetApptData?.grooming?.service && !currentPetApptData?.hotel?.roomType && (
                            <div className="p-4 bg-gray-50 rounded-2xl border border-gray-200 text-xs">
                              <span className="text-gray-400 font-bold uppercase block text-[10px]">Service Type</span>
                              <span className="text-sm font-bold text-gray-800 capitalize">
                                {currentPetApptData?.serviceType || "Standard Appointment"}
                              </span>
                            </div>
                          )}
                        </div>

                        {/* Payment & Financial Breakdown */}
                        <div className="p-4 rounded-2xl bg-emerald-50/60 border border-emerald-200 space-y-3">
                          <div className="flex items-center justify-between">
                            <span className="text-xs font-black text-emerald-800 uppercase tracking-wider flex items-center gap-1.5">
                              <span>💳</span> Billing &amp; Counter Collection
                            </span>
                            <span className="text-xs font-black text-gray-700">
                              Method: {currentAppt.paymentMethod || "GCash"}
                            </span>
                          </div>

                          <div className="grid grid-cols-3 gap-2 text-center">
                            <div className="bg-white p-2.5 rounded-xl border border-emerald-100">
                              <span className="text-[10px] font-bold text-gray-400 uppercase block">Total Bill</span>
                              <span className="text-base font-black text-gray-800">
                                ₱{(currentAppt.totalPrice || 0).toLocaleString()}
                              </span>
                            </div>
                            <div className="bg-white p-2.5 rounded-xl border border-emerald-100">
                              <span className="text-[10px] font-bold text-gray-400 uppercase block">Deposit Paid</span>
                              <span className="text-base font-black text-emerald-600">
                                ₱{(currentAppt.downpaymentAmount || 0).toLocaleString()}
                              </span>
                            </div>
                            <div className="bg-white p-2.5 rounded-xl border-2 border-emerald-300 shadow-sm">
                              <span className="text-[10px] font-black text-emerald-700 uppercase block">
                                Balance Due
                              </span>
                              <span className="text-base font-black text-[#46735c]">
                                ₱{Math.max(
                                  0,
                                  (currentAppt.totalPrice || 0) - (currentAppt.downpaymentAmount || 0)
                                ).toLocaleString()}
                              </span>
                            </div>
                          </div>

                          {currentAppt.referenceNumber && (
                            <div className="flex items-center justify-between text-xs bg-white p-2.5 rounded-xl border border-emerald-100">
                              <span className="text-gray-500 font-medium">Payment Reference #:</span>
                              <span className="font-mono font-bold text-gray-800">{currentAppt.referenceNumber}</span>
                            </div>
                          )}

                          {currentAppt.paymentScreenshot && (
                            <div className="pt-1">
                              <span className="text-[10px] font-bold text-gray-500 uppercase block mb-1">
                                Payment Proof (Click to enlarge):
                              </span>
                              <img
                                src={currentAppt.paymentScreenshot}
                                alt="Payment Proof"
                                onClick={() => setZoomImage(currentAppt.paymentScreenshot)}
                                className="h-16 rounded-xl border border-emerald-200 object-cover cursor-zoom-in hover:opacity-90 bg-white"
                              />
                            </div>
                          )}
                        </div>

                        {/* Customer Notes */}
                        {currentAppt.notes && (
                          <div className="p-3.5 bg-gray-50 rounded-2xl border border-gray-200 text-xs">
                            <span className="text-[10px] font-bold text-gray-400 uppercase block mb-0.5">
                              Customer Notes / Requests
                            </span>
                            <p className="text-gray-700 italic">"{currentAppt.notes}"</p>
                          </div>
                        )}

                        {/* Quick Reception Action Buttons */}
                        <div className="pt-2 border-t border-gray-100 flex flex-wrap items-center justify-end gap-2">
                          {/* If Pending: Approve / Reject */}
                          {currentAppt.status === "pending" && (
                            <>
                              <button
                                disabled={isUpdatingStatus}
                                onClick={() => handleQuickStatusChange(currentAppt._id, "rejected")}
                                className="px-4 py-2.5 bg-red-50 hover:bg-red-100 text-red-600 font-bold rounded-xl text-xs transition-colors border border-red-200"
                              >
                                Reject Booking
                              </button>
                              <button
                                disabled={isUpdatingStatus}
                                onClick={() => handleQuickStatusChange(currentAppt._id, "confirmed")}
                                className="px-5 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-xl text-xs shadow-md transition-all flex items-center gap-1.5"
                              >
                                <span>✅</span> Approve Booking
                              </button>
                            </>
                          )}

                          {/* If Confirmed: Mark Ongoing */}
                          {currentAppt.status === "confirmed" && (
                            <button
                              disabled={isUpdatingStatus}
                              onClick={() => handleQuickStatusChange(currentAppt._id, "ongoing")}
                              className="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl text-xs shadow-md transition-all flex items-center gap-1.5"
                            >
                              <span>✂️</span> Check-In &amp; Start Service (Mark Ongoing)
                            </button>
                          )}

                          {/* If Ongoing: Toggle Ready for Pickup or Mark Completed */}
                          {currentAppt.status === "ongoing" && (
                            <>
                              <button
                                disabled={isUpdatingStatus}
                                onClick={() =>
                                  handleTogglePickupStatus(currentAppt._id, currentAppt.isReadyForPickup)
                                }
                                className={`px-4 py-2.5 rounded-xl text-xs font-bold transition-all shadow-sm flex items-center gap-1.5 ${
                                  currentAppt.isReadyForPickup
                                    ? "bg-purple-100 text-purple-800 border-2 border-purple-300"
                                    : "bg-purple-600 hover:bg-purple-700 text-white"
                                }`}
                              >
                                <span>🛍️</span>
                                {currentAppt.isReadyForPickup
                                  ? "Ready for Pickup (Notified)"
                                  : "Mark Ready for Pickup"}
                              </button>
                              <button
                                disabled={isUpdatingStatus}
                                onClick={() => handleQuickStatusChange(currentAppt._id, "completed")}
                                className="px-5 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-xl text-xs shadow-md transition-all flex items-center gap-1.5"
                              >
                                <span>🎉</span> Complete &amp; Check Out
                              </button>
                            </>
                          )}

                          {/* If Ready for Pickup & Completed */}
                          {currentAppt.status === "completed" && (
                            <span className="px-4 py-2 bg-emerald-100 text-emerald-800 font-bold rounded-xl text-xs border border-emerald-300 flex items-center gap-1.5">
                              <span>🎉</span> Appointment Finished
                            </span>
                          )}

                          {/* Edit in Full View */}
                          {onEditAppointment && (
                            <button
                              onClick={() => {
                                onEditAppointment(currentAppt);
                                onClose();
                              }}
                              className="px-4 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 font-bold rounded-xl text-xs transition-colors flex items-center gap-1.5"
                            >
                              <EditIcon size={13} /> Check Details
                            </button>
                          )}
                        </div>
                      </div>
                    ) : (
                      /* No Active Appointment */
                      <div className="bg-white rounded-3xl p-8 border border-gray-200 text-center space-y-3">
                        <div className="w-14 h-14 rounded-full bg-purple-50 text-purple-500 flex items-center justify-center mx-auto text-2xl">
                          📅
                        </div>
                        <h4 className="text-base font-bold text-gray-800">
                          No Active Appointment Scheduled
                        </h4>
                        <p className="text-xs text-gray-500 max-w-sm mx-auto">
                          {pet.petName} does not have a pending, confirmed, or ongoing appointment right now.
                        </p>
                        <div className="pt-2 flex justify-center gap-3">
                          <button
                            onClick={() => setTab("details")}
                            className="px-4 py-2 bg-purple-100 hover:bg-purple-200 text-purple-700 font-bold rounded-xl text-xs transition-colors"
                          >
                            View Pet Details
                          </button>
                          <button
                            onClick={() => setTab("history")}
                            className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-bold rounded-xl text-xs transition-colors"
                          >
                            View Visit History
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* ════════════════════════════════════════════════════════════════
                    TAB 2: DETAILS (PET, MEDICAL & OWNER PROFILE)
                ════════════════════════════════════════════════════════════════ */}
                {tab === "details" && (
                  <div className="space-y-4 animate-in fade-in duration-200">
                    {/* Pet Overview Grid */}
                    <div className="grid grid-cols-2 gap-3 text-xs">
                      <div className="bg-white p-3.5 rounded-2xl border border-purple-100 shadow-sm">
                        <span className="text-[10px] font-black uppercase tracking-wider text-purple-500 flex items-center gap-1 mb-1">
                          <ClockIcon size={13} color="currentColor" /> Calculated Age
                        </span>
                        <p className="text-sm font-black text-gray-800">{formatRealAge(realAge)}</p>
                      </div>

                      <div className="bg-white p-3.5 rounded-2xl border border-purple-100 shadow-sm">
                        <span className="text-[10px] font-black uppercase tracking-wider text-purple-500 flex items-center gap-1 mb-1">
                          <PawIcon size={13} color="currentColor" /> Total Completed Visits
                        </span>
                        <p className="text-sm font-black text-emerald-700">{completedVisits} visits</p>
                      </div>

                      <div className="col-span-2 bg-white p-3.5 rounded-2xl border border-purple-100 shadow-sm">
                        <span className="text-[10px] font-black uppercase tracking-wider text-purple-500 flex items-center gap-1 mb-1">
                          <TagIcon size={13} color="currentColor" /> Color &amp; Distinguishing Markings
                        </span>
                        <p className="text-xs font-bold text-gray-800">{pet.colorMarkings || "None noted"}</p>
                      </div>

                      {pet.birthday && (
                        <div className="col-span-2 bg-white p-3.5 rounded-2xl border border-purple-100 shadow-sm">
                          <span className="text-[10px] font-black uppercase tracking-wider text-purple-500 flex items-center gap-1 mb-1">
                            <CalendarIcon size={13} color="currentColor" /> Birthday
                          </span>
                          <p className="text-xs font-bold text-gray-800">
                            {new Date(pet.birthday).toLocaleDateString("en-US", {
                              month: "long",
                              day: "numeric",
                              year: "numeric",
                            })}
                          </p>
                        </div>
                      )}
                    </div>

                    {/* Behavior & Temperament Notes */}
                    {pet.behaviorNotes ? (
                      <div className="bg-amber-50 rounded-2xl p-4 border border-amber-200 space-y-1">
                        <p className="text-xs font-black uppercase tracking-widest text-amber-700 flex items-center gap-1.5">
                          <span>⚠️</span> Behavior &amp; Handling Notes
                        </p>
                        <p className="text-xs text-amber-950 font-medium leading-relaxed">
                          {pet.behaviorNotes}
                        </p>
                      </div>
                    ) : (
                      <div className="bg-gray-50 rounded-2xl p-3.5 border border-gray-200 text-xs text-gray-500">
                        No specific behavioral issues reported for {pet.petName}.
                      </div>
                    )}

                    {/* Medical & Vaccination Records */}
                    <div className="bg-white rounded-2xl p-4 border border-purple-100 shadow-sm space-y-3">
                      <div className="flex items-center justify-between pb-2 border-b border-gray-100">
                        <span className="text-xs font-black uppercase tracking-wider text-emerald-600 flex items-center gap-1.5">
                          <ShieldCheckIcon size={16} color="currentColor" /> Health &amp; Vaccination Status
                        </span>
                        <span
                          className={`text-[10px] font-black uppercase px-2.5 py-0.5 rounded-full ${
                            pet.health?.isVaccinated
                              ? "bg-emerald-100 text-emerald-700 border border-emerald-200"
                              : "bg-red-100 text-red-700 border border-red-200"
                          }`}
                        >
                          {pet.health?.isVaccinated ? "✅ Vaccinated" : "⚠️ Not Vaccinated"}
                        </span>
                      </div>

                      {pet.health?.lastVaccinationDate && (
                        <p className="text-xs text-gray-600">
                          <span className="font-bold">Last Vaccination Date:</span>{" "}
                          {new Date(pet.health.lastVaccinationDate).toLocaleDateString("en-US", {
                            month: "short",
                            day: "numeric",
                            year: "numeric",
                          })}
                        </p>
                      )}

                      {pet.health?.medicalConditions && (
                        <div className="bg-red-50/60 p-3 rounded-xl border border-red-100 text-xs">
                          <span className="text-[10px] font-bold text-red-700 uppercase block mb-0.5">
                            Medical Conditions / Allergies:
                          </span>
                          <p className="text-red-900 font-medium">{pet.health.medicalConditions}</p>
                        </div>
                      )}

                      {pet.health?.medication && (
                        <div className="bg-blue-50/60 p-3 rounded-xl border border-blue-100 text-xs">
                          <span className="text-[10px] font-bold text-blue-700 uppercase block mb-0.5">
                            Ongoing Medications &amp; Care:
                          </span>
                          <p className="text-blue-900 font-medium">{pet.health.medication}</p>
                        </div>
                      )}

                      {pet.health?.vaccinationPhotoUrl && (
                        <div className="pt-2">
                          <span className="text-[10px] font-bold text-gray-500 uppercase block mb-1.5">
                            Vaccination Card / Medical Certificate (Click to expand):
                          </span>
                          <img
                            src={pet.health.vaccinationPhotoUrl}
                            alt="Vaccination Record"
                            onClick={() => setZoomImage(pet.health.vaccinationPhotoUrl)}
                            className="max-h-36 rounded-xl border-2 border-emerald-200 object-cover cursor-zoom-in hover:opacity-90 transition-opacity bg-white"
                          />
                        </div>
                      )}
                    </div>

                    {/* Owner Information */}
                    <div className="bg-white rounded-2xl p-4 border border-purple-100 shadow-sm space-y-2 text-xs">
                      <span className="text-xs font-black uppercase tracking-wider text-gray-700 block">
                        Customer Contact Information
                      </span>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-gray-700">
                        <div className="bg-gray-50 p-2.5 rounded-xl">
                          <span className="text-[10px] text-gray-400 font-bold uppercase block">Full Name</span>
                          <span className="font-bold text-gray-800">{ownerInfo.name || "Customer"}</span>
                        </div>
                        <div className="bg-gray-50 p-2.5 rounded-xl">
                          <span className="text-[10px] text-gray-400 font-bold uppercase block">Phone</span>
                          <span className="font-bold text-gray-800">{ownerInfo.phone || "Not on file"}</span>
                        </div>
                        <div className="col-span-1 sm:col-span-2 bg-gray-50 p-2.5 rounded-xl">
                          <span className="text-[10px] text-gray-400 font-bold uppercase block">Email Address</span>
                          <span className="font-bold text-gray-800">{ownerInfo.email || "Not on file"}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* ════════════════════════════════════════════════════════════════
                    TAB 3: HISTORY (PAST BOOKINGS & VISITS)
                ════════════════════════════════════════════════════════════════ */}
                {tab === "history" && (
                  <div className="space-y-3 animate-in fade-in duration-200">
                    <div className="flex items-center justify-between px-1">
                      <span className="text-xs font-black text-gray-500 uppercase tracking-wider">
                        All Past Visits ({pastAppointments.length})
                      </span>
                      <span className="text-xs font-bold text-emerald-700 bg-emerald-50 px-2.5 py-0.5 rounded-full border border-emerald-100">
                        {completedVisits} successfully completed
                      </span>
                    </div>

                    {pastAppointments.length === 0 ? (
                      <div className="bg-white rounded-3xl p-10 border border-gray-200 text-center text-gray-400 space-y-2">
                        <PawIcon size={36} color="#d1d5db" className="mx-auto" />
                        <p className="text-sm font-bold text-gray-600">No Past Booking Records</p>
                        <p className="text-xs text-gray-400">
                          Previous appointments and completed visits will appear here.
                        </p>
                      </div>
                    ) : (
                      pastAppointments.map((apt) => {
                        const cfg = STATUS_CONFIG[apt.status] || STATUS_CONFIG.completed;
                        const isExpanded = expandedHistoryId === apt._id;
                        const petData = getPetDetailsInAppt(apt, pet);

                        return (
                          <div
                            key={apt._id}
                            className={`rounded-2xl border transition-all bg-white overflow-hidden shadow-sm ${
                              isExpanded ? "border-purple-300 ring-2 ring-purple-100" : "border-gray-200"
                            }`}
                          >
                            {/* History Item Header */}
                            <div
                              onClick={() => setExpandedHistoryId(isExpanded ? null : apt._id)}
                              className="p-4 cursor-pointer hover:bg-gray-50/80 transition-colors flex items-center justify-between gap-3"
                            >
                              <div className="flex-1 min-w-0 space-y-1">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span
                                    className={`text-[10px] font-black uppercase px-2 py-0.5 rounded-full ${cfg.bg} ${cfg.text}`}
                                  >
                                    {cfg.emoji} {cfg.label}
                                  </span>
                                  <span className="text-xs text-gray-400 font-bold">
                                    {apt.appointmentDate
                                      ? new Date(apt.appointmentDate).toLocaleDateString("en-US", {
                                          month: "short",
                                          day: "numeric",
                                          year: "numeric",
                                        })
                                      : new Date(apt.createdAt).toLocaleDateString()}
                                    {apt.appointmentTime && ` at ${apt.appointmentTime}`}
                                  </span>
                                </div>

                                <p className="text-sm font-bold text-gray-800 truncate">
                                  {petData?.grooming?.service ||
                                    (petData?.hotel?.roomType ? `Hotel: ${petData.hotel.roomType}` : "") ||
                                    apt.pets?.map((p) => p.petName).join(", ") ||
                                    "Furpect Tails Visit"}
                                </p>

                                {petData?.grooming?.haircutStyle && (
                                  <p className="text-xs text-purple-600 font-semibold">
                                    Style: {petData.grooming.haircutStyle}
                                  </p>
                                )}
                              </div>

                              <div className="text-right shrink-0 flex items-center gap-3">
                                <div>
                                  <span className="text-sm font-black text-gray-800 block">
                                    ₱{(apt.totalPrice || 0).toLocaleString()}
                                  </span>
                                  <span className="text-[10px] text-gray-400 font-medium">
                                    {apt.paymentMethod || "Cash"}
                                  </span>
                                </div>
                                <div className={`transition-transform duration-200 ${isExpanded ? "rotate-180" : ""}`}>
                                  <ChevronDownIcon size={16} color="#9ca3af" />
                                </div>
                              </div>
                            </div>

                            {/* Expanded Record Details */}
                            {isExpanded && (
                              <div className="p-4 bg-[#faf9f7] border-t border-gray-100 text-xs space-y-3">
                                <div className="grid grid-cols-2 gap-2">
                                  <div className="bg-white p-2.5 rounded-xl border border-gray-200">
                                    <span className="text-[10px] font-bold text-gray-400 uppercase block">
                                      Service Rendered
                                    </span>
                                    <span className="font-bold text-gray-800">
                                      {petData?.grooming?.service || petData?.serviceType || "Grooming"}
                                    </span>
                                  </div>

                                  {petData?.grooming?.haircutStyle && (
                                    <div className="bg-white p-2.5 rounded-xl border border-gray-200">
                                      <span className="text-[10px] font-bold text-gray-400 uppercase block">
                                        Haircut Style
                                      </span>
                                      <span className="font-bold text-gray-800">
                                        {petData.grooming.haircutStyle}
                                      </span>
                                    </div>
                                  )}

                                  {petData?.hotel?.roomType && (
                                    <div className="bg-white p-2.5 rounded-xl border border-gray-200 col-span-2">
                                      <span className="text-[10px] font-bold text-gray-400 uppercase block">
                                        Hotel Stay
                                      </span>
                                      <span className="font-bold text-gray-800">
                                        {petData.hotel.roomType} · {petData.hotel.stayDays || 1} day(s)
                                      </span>
                                    </div>
                                  )}
                                </div>

                                {petData?.grooming?.addOns && petData.grooming.addOns.length > 0 && (
                                  <div className="bg-white p-2.5 rounded-xl border border-gray-200">
                                    <span className="text-[10px] font-bold text-gray-400 uppercase block mb-1">
                                      Add-Ons Included
                                    </span>
                                    <div className="flex flex-wrap gap-1">
                                      {petData.grooming.addOns.map((add, idx) => (
                                        <span
                                          key={idx}
                                          className="bg-gray-100 text-gray-700 text-[11px] font-semibold px-2 py-0.5 rounded-md"
                                        >
                                          {add.name}
                                        </span>
                                      ))}
                                    </div>
                                  </div>
                                )}

                                {apt.rating && (
                                  <div className="bg-pink-50 p-2.5 rounded-xl border border-pink-100 flex items-center gap-2">
                                    <span className="text-amber-500 font-bold">
                                      {"★".repeat(apt.rating)}
                                      {"☆".repeat(Math.max(0, 5 - apt.rating))}
                                    </span>
                                    {apt.feedback && (
                                      <span className="text-xs text-pink-900 italic font-medium truncate">
                                        "{apt.feedback}"
                                      </span>
                                    )}
                                  </div>
                                )}

                                {apt.rejectReason && (
                                  <div className="bg-red-50 p-2.5 rounded-xl border border-red-100 text-red-800">
                                    <strong>Rejection Reason:</strong> {apt.rejectReason}
                                  </div>
                                )}

                                {apt.cancelReason && (
                                  <div className="bg-gray-100 p-2.5 rounded-xl text-gray-700">
                                    <strong>Cancellation Reason:</strong> {apt.cancelReason}
                                  </div>
                                )}

                                {apt.referenceNumber && (
                                  <div className="text-[11px] text-gray-500">
                                    Ref #: <strong className="font-mono text-gray-700">{apt.referenceNumber}</strong>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })
                    )}
                  </div>
                )}

                {/* ── Bottom Link: View all in Appointments Tab ── */}
                {onViewBookings && (
                  <button
                    onClick={() => onViewBookings(pet.petName, pet.petType)}
                    className="w-full py-3 bg-white border-2 border-purple-200 text-purple-700 font-bold rounded-2xl hover:bg-purple-50 transition-all flex items-center justify-center gap-2 text-xs shadow-sm"
                  >
                    <ShieldCheckIcon size={16} color="currentColor" />
                    Open All Bookings for {pet.petName} in Admin Appointments
                  </button>
                )}
              </>
            ) : null}
          </div>
        </div>
      </div>

      {/* ── Image Lightbox Modal for Photo / Vaccine / Receipts ── */}
      {zoomImage && (
        <div
          className="fixed inset-0 bg-black/85 backdrop-blur-sm z-[100] flex items-center justify-center p-4 cursor-zoom-out animate-in fade-in duration-200"
          onClick={() => setZoomImage(null)}
        >
          <div
            className="relative max-w-4xl max-h-[85vh] flex flex-col items-center justify-center"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setZoomImage(null)}
              className="absolute -top-12 right-0 bg-white/20 hover:bg-white/30 text-white rounded-full w-10 h-10 flex items-center justify-center text-2xl font-bold transition-all shadow-md"
            >
              ×
            </button>
            <img
              src={zoomImage}
              alt="Enlarged Record"
              className="max-w-full max-h-[80vh] rounded-2xl object-contain shadow-2xl border-4 border-white/10"
            />
            <p className="text-white/70 text-xs mt-3 font-semibold tracking-wider">
              Click anywhere outside to close
            </p>
          </div>
        </div>
      )}
    </>
  );
};

export default PetInfoModal;
